import type { Employee, RequestField, TimeOff, User } from "../lib/types";
import { TIME_OFF_LABEL } from "../lib/types";

function reviewerName(
  userId: string | null,
  usersById: Record<string, User>,
  empById: Record<string, Employee>,
): string {
  if (!userId) return "Unknown";
  const u = usersById[userId];
  if (!u) return "Unknown";
  const emp = u.employeeId ? empById[u.employeeId] : undefined;
  if (emp) return `${emp.lastName}, ${emp.firstName}`;
  return u.username || u.email || "Unknown";
}

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

// One printable form per completed request, hidden on screen and
// revealed only inside the browser print dialog. The Training and
// Time-off pages drop this component into the page; their on-screen
// list and chrome are already `print:hidden`, so when the supervisor
// hits Print / PDF the paper output is just these forms — one per
// page, with a status banner the agency can file.
//
// When `onlyId` is set, only that single request prints — used by
// the per-row Export button on completed rows. When null, every
// request in `requests` prints (the page-level "Print completed"
// flow).
//
// Active (still-pending) requests are intentionally excluded from
// the page-level export; the printable artefact is the *decision*,
// not the queue. A per-row Export only appears once a request is
// approved or denied.
export function PrintableForms({
  agencyName, kindLabel, requests, fields, empById, onlyId,
  usersById = {},
}: {
  agencyName: string;
  kindLabel: "Training" | "Time off";
  requests: TimeOff[];
  fields: RequestField[];
  empById: Record<string, Employee>;
  onlyId?: string | null;
  usersById?: Record<string, User>;
}) {
  const list = onlyId
    ? requests.filter((r) => r.id === onlyId)
    : requests;
  if (list.length === 0) {
    return (
      <div className="hidden print:block p-8 text-center text-sm text-slate-500">
        No completed {kindLabel.toLowerCase()} requests to print.
      </div>
    );
  }
  return (
    <div className="hidden print:block">
      {list.map((r, i) => (
        <PrintableForm
          key={r.id}
          agencyName={agencyName}
          kindLabel={kindLabel}
          request={r}
          employee={empById[r.employeeId]}
          fields={fields}
          usersById={usersById}
          empById={empById}
          isLast={i === list.length - 1}
        />
      ))}
    </div>
  );
}

function PrintableForm({
  agencyName, kindLabel, request, employee, fields, isLast,
  usersById, empById,
}: {
  agencyName: string;
  kindLabel: "Training" | "Time off";
  request: TimeOff;
  employee: Employee | undefined;
  fields: RequestField[];
  isLast: boolean;
  usersById: Record<string, User>;
  empById: Record<string, Employee>;
}) {
  const filled = fields
    .map((f) => ({ f, v: request.metadata?.[f.key] }))
    .filter(({ v, f }) => {
      if (f.fieldType === "checkbox") return v === true;
      return v !== undefined && v !== null && v !== "";
    });

  const statusColor =
    request.status === "approved" ? "#15803d" :
    request.status === "denied"   ? "#b91c1c" :
                                    "#b45309";
  const statusBg =
    request.status === "approved" ? "#dcfce7" :
    request.status === "denied"   ? "#fee2e2" :
                                    "#fef3c7";
  const statusLabel = request.status.toUpperCase();

  return (
    <article
      className="text-slate-900"
      style={{
        // One form per printed page. The last form skips the page
        // break so we don't end on a stray blank page in the PDF.
        pageBreakAfter: isLast ? undefined : "always",
        padding: "1.25rem 1.5rem",
        fontSize: "11pt",
        lineHeight: 1.45,
        color: "#0f172a",            // near-black body text
        backgroundColor: "#ffffff",  // force white even in dark mode
      }}
    >
      <header
        style={{
          borderBottom: "1.5px solid #475569",
          paddingBottom: "0.5rem",
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <div>
          <div style={{ fontSize: "13pt", fontWeight: 700, color: "#0f172a" }}>
            {agencyName}
          </div>
          <div style={{ fontSize: "10pt", color: "#334155", fontWeight: 500 }}>
            {kindLabel} request
          </div>
        </div>
        <div style={{ fontSize: "9pt", color: "#475569", fontFamily: "monospace" }}>
          ID {request.id.slice(0, 8)}
        </div>
      </header>

      <FormRow label="Officer">
        {employee
          ? `${employee.lastName}, ${employee.firstName}`
            + (employee.rank ? ` · ${employee.rank}` : "")
            + (employee.badgeNumber ? ` · #${employee.badgeNumber}` : "")
          : request.employeeId}
      </FormRow>
      <FormRow label="Submitted">
        {request.createdAt.slice(0, 10)}
      </FormRow>
      <FormRow label="Type">
        {kindLabel === "Training" ? "Training" : TIME_OFF_LABEL[request.type]}
      </FormRow>
      <FormRow label="Dates">
        {request.startDate} → {request.endDate}
      </FormRow>

      {filled.length > 0 && (
        <section style={{ marginTop: "0.75rem" }}>
          <div style={{
            fontSize: "9pt",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#334155",
            fontWeight: 600,
            marginBottom: "0.25rem",
          }}>
            Details
          </div>
          {filled.map(({ f, v }) => (
            <FormRow key={f.id} label={f.label}>
              {f.fieldType === "checkbox" ? "Yes" : String(v)}
            </FormRow>
          ))}
        </section>
      )}

      {request.note && (
        <section style={{ marginTop: "0.75rem" }}>
          <div style={{
            fontSize: "9pt",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#334155",
            fontWeight: 600,
          }}>
            Note
          </div>
          <p style={{ marginTop: "0.25rem", whiteSpace: "pre-wrap" }}>
            {request.note}
          </p>
        </section>
      )}

      <section
        style={{
          marginTop: "1rem",
          padding: "0.5rem 0.75rem",
          backgroundColor: statusBg,
          color: statusColor,
          border: `1px solid ${statusColor}`,
          borderRadius: "4px",
          fontWeight: 600,
          letterSpacing: "0.05em",
        }}
      >
        STATUS: {statusLabel}
      </section>

      <section style={{ marginTop: "0.75rem" }}>
        {kindLabel === "Training" ? (
          <>
            <FormRow label="L2 approval">
              {request.l2ApprovedBy
                ? `${reviewerName(request.l2ApprovedBy, usersById, empById)}${
                    request.l2ApprovedAt ? ` · ${fmtDate(request.l2ApprovedAt)}` : ""
                  }`
                : request.status === "approved"
                  ? "Skipped — Level 1 approved directly"
                  : "—"}
            </FormRow>
            <FormRow label={request.status === "denied" ? "Denied by" : "Final approval"}>
              {request.reviewedBy
                ? `${reviewerName(request.reviewedBy, usersById, empById)}${
                    request.reviewedAt ? ` · ${fmtDate(request.reviewedAt)}` : ""
                  }`
                : "Unknown"}
            </FormRow>
          </>
        ) : (
          <FormRow label={request.status === "denied" ? "Denied by" : "Approved by"}>
            {request.reviewedBy
              ? `${reviewerName(request.reviewedBy, usersById, empById)}${
                  request.reviewedAt ? ` · ${fmtDate(request.reviewedAt)}` : ""
                }`
              : "Unknown"}
          </FormRow>
        )}
      </section>

      <section style={{ marginTop: "1.5rem", fontSize: "10pt" }}>
        <SignatureLine label="Officer signature" />
        <SignatureLine label="Reviewing supervisor" />
      </section>
    </article>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: "0.75rem", padding: "0.15rem 0" }}>
      <div style={{
        width: "10rem",
        flexShrink: 0,
        fontSize: "9pt",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#334155",
        fontWeight: 600,
        paddingTop: "0.1rem",
      }}>
        {label}
      </div>
      <div style={{ flex: 1, color: "#0f172a" }}>{children}</div>
    </div>
  );
}

function SignatureLine({ label }: { label: string }) {
  return (
    <div style={{
      display: "flex",
      gap: "1.5rem",
      marginTop: "1rem",
      alignItems: "flex-end",
    }}>
      <div style={{ flex: 2 }}>
        <div style={{ borderBottom: "1.5px solid #475569", height: "1.25rem" }} />
        <div style={{ fontSize: "8pt", color: "#334155", marginTop: "0.15rem", fontWeight: 500 }}>
          {label}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ borderBottom: "1.5px solid #475569", height: "1.25rem" }} />
        <div style={{ fontSize: "8pt", color: "#334155", marginTop: "0.15rem", fontWeight: 500 }}>
          Date
        </div>
      </div>
    </div>
  );
}
